// Serviciu Account CRUD pentru conturi utilizator
// OWNER: echipa API & FE
// Orice modificare trebuie adăugată în DEV_LOG.md

import { supabase } from "./supabase";

export interface AccountApi {
  id: string;
  user_id: string;
  name: string;
  account_type: string; // Keeping string, map to enum in UI
  initial_balance: number;
  current_balance: number;
  is_active: boolean;
  included_in_total: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface CreateAccountPayload {
  name: string;
  account_type?: string; // default CHECKING
  initial_balance?: number;
  included_in_total?: boolean;
  display_order?: number;
}

export interface UpdateAccountPayload {
  name?: string;
  account_type?: string;
  initial_balance?: number;
  current_balance?: number;
  is_active?: boolean;
  included_in_total?: boolean;
  display_order?: number;
}

const TABLE = "user_accounts";

/** Helper simplu retry cu backoff liniar (pragmatic) */
async function runWithRetry<T>(fn: () => Promise<T>, retries = 2, delayMs = 500): Promise<T> {
  let lastErr: Error | unknown;
  for (let i = 0; i <= retries; i++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (i < retries) {
        await new Promise((res) => setTimeout(res, delayMs * (i + 1)));
      }
    }
  }
  throw lastErr;
}

export const accountService = {
  /**
   * Returnează toate conturile pentru user-ul curent.
   * @param userId id-ul user-ului autentificat (auth.users.id)
   */
  async fetchAccounts(userId: string | undefined): Promise<AccountApi[]> {
    let query = supabase.from(TABLE).select("*").order("display_order", { ascending: true });

    if (userId) {
      query = query.eq("user_id", userId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /** Creează un cont nou */
  async createAccount(userId: string, payload: CreateAccountPayload): Promise<AccountApi> {
    const insertObj = {
      user_id: userId,
      name: payload.name,
      account_type: payload.account_type ?? "CHECKING",
      initial_balance: payload.initial_balance ?? 0,
      current_balance: payload.initial_balance ?? 0,
      included_in_total: payload.included_in_total ?? true,
      display_order: payload.display_order ?? 0,
      is_active: true,
    };

    const insertFn = async () => {
      const { data, error } = await supabase.from(TABLE).insert(insertObj).select().single();
      if (error) throw error;
      return data;
    };
    return runWithRetry(insertFn);
  },

  /** Update cont */
  async updateAccount(id: string, payload: UpdateAccountPayload): Promise<AccountApi> {
    const fn = async () => {
      const { data, error } = await supabase.from(TABLE).update(payload).eq("id", id).select().single();
      if (error) throw error;
      return data;
    };
    return runWithRetry(fn);
  },

  /** Șterge cont */
  async deleteAccount(id: string): Promise<void> {
    const fn = async () => {
      const { error } = await supabase.from(TABLE).delete().eq("id", id);
      if (error) throw error;
    };
    await runWithRetry(fn);
  },
};
